import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../../../hooks/redux';
import { macAddressExample } from '../../../../../constants/configuration';
import { FormInputText } from '../../../../inputs/FormInputText';
import { ButtonClassNames, ModalVariants, InputRegexPattern } from '../../../../../models/Common';
import { CustomMUIButton } from '../../../buttons/CustomMUIButton';
import { ApiError } from '../../../../../models/ApiError';
import { modalSlice } from '../../../../../store/reducers/ModalSlice';
import { internetApi } from '../../../../../services/apiServices/user/internet';
import { useCustomSnackbar } from '../../../../../hooks/useCustomEnqueueSnackbar';
import { ConnectionProtocolTypes } from '../../../../../models/Accounts';
import { getPpoeSettingsLink, getErrorMessage } from '../../../../../utils';
import { FormInputPassword } from '../../../../inputs/FormInputPassword';

interface EditInternetMacAddressProps {
  accountId: string | number;
  mac: string;
  handleClose: () => void;
  connectionProtocolId: number;
  selectedLanguage: string;
}

interface EditInternetMacAddressForm {
  macAddress: string;
  currentPassword: string;
}

export const EditInternetMacAddress: FC<EditInternetMacAddressProps> = ({
  accountId,
  mac,
  handleClose,
  connectionProtocolId,
  selectedLanguage,
}) => {
  const { t } = useTranslation();
  const methods = useForm<EditInternetMacAddressForm>({ defaultValues: { macAddress: mac, currentPassword: '' } });
  const { handleSubmit, setError } = methods;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [editInternetDeviceMac, { isLoading }] = internetApi.useChangeMacMutation();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;

  const handleEditMac = async ({ macAddress, currentPassword }: EditInternetMacAddressForm) => {
    try {
      await editInternetDeviceMac({ mac: macAddress, accountId, currentPassword })
        .unwrap()
        .then(() => {
          customEnqueueSnackbar(t('snackbar.internetDeviceMacChanged'), 'success');
          handleClose();
        });
    } catch (err) {
      const error = err as ApiError;
      if (error?.data?.error?.errors) {
        const apiErrors = error.data.error.errors as Partial<Record<keyof EditInternetMacAddressForm, string>>;
        const apiErrorsKeys = Object.keys(apiErrors) as Array<keyof EditInternetMacAddressForm>;
        apiErrorsKeys.forEach((element) => {
          setError(element, {
            type: 'server',
            message: apiErrors[element],
          });
        });
      } else {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  };

  const handleForgotPassword = () => {
    dispatch(openModal({ modalVariants: ModalVariants.LogOut }));
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{t('modals.changingMacAddress')}</article>
        {connectionProtocolId === ConnectionProtocolTypes.PPPoE && (
          <div className="modals__attention">
            {t('serviceManagement.afterChangingDataInternetConnectionWillBeInterrupted')}{' '}
            <a href={getPpoeSettingsLink(selectedLanguage)} rel="noreferrer" target="_blank">
              {t('serviceManagement.pppoeConnectionSettings')}
            </a>
          </div>
        )}
      </div>
      <FormProvider {...methods}>
        <form className="modals__form" onSubmit={handleSubmit(handleEditMac)}>
          <FormInputPassword
            name="currentPassword"
            className="primary"
            label={t('auth.enterPassLk')}
            isRequired
            minLength={6}
            maxLength={16}
            fieldHasName
          />
          <FormInputText
            name="macAddress"
            className="primary"
            label={t('input.macAddress')}
            helperText={t('input.example', { val: macAddressExample })}
            isRequired
            minLength={17}
            maxLength={17}
            pattern={InputRegexPattern.MacAddress}
          />
          <div className="modals__btns-wrapper">
            <div className="modals__btn-group">
              <CustomMUIButton
                label={t('buttons.save')}
                type="submit"
                className={ButtonClassNames.PrimaryFulfilled}
                isBold
                isLoading={isLoading}
              />
            </div>
            <div className="modals__btn-group">
              <CustomMUIButton
                label={t('auth.forgotMyPassword')}
                handleClick={handleForgotPassword}
                className={ButtonClassNames.SecondaryOutlined}
                isBold
                type="button"
              />
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
